const RETURN_URL_STORAGE_KEY = "reliablyme:returnUrl";

/** Safe internal return path after login (blocks open redirects). */
export function getSafeReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith("/")) return null;
    if (decoded.startsWith("//")) return null;
    if (/^https?:/i.test(decoded)) return null;
    if (decoded.includes("://")) return null;
    if (decoded.startsWith("/login")) return null;
    return decoded;
  } catch {
    return null;
  }
}

export function persistReturnUrl(url: string | null | undefined): void {
  const safe = getSafeReturnUrl(url);
  if (safe) {
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, safe);
  }
}

/** Query param first, then sessionStorage fallback (survives reload on login page). */
export function resolveReturnUrl(searchParam?: string | null): string | null {
  const fromQuery = getSafeReturnUrl(searchParam);
  if (fromQuery) return fromQuery;
  return getSafeReturnUrl(sessionStorage.getItem(RETURN_URL_STORAGE_KEY));
}

export function clearPersistedReturnUrl(): void {
  sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
}

/** Resolve return path and clear stored copy so it is only used once. */
export function consumeReturnUrl(searchParam?: string | null): string | null {
  const resolved = resolveReturnUrl(searchParam);
  clearPersistedReturnUrl();
  return resolved;
}

export function buildLoginUrl(options?: {
  timeout?: boolean;
  returnUrl?: string;
}): string {
  const params = new URLSearchParams();
  if (options?.timeout) params.set("timeout", "true");
  const safe = getSafeReturnUrl(options?.returnUrl);
  if (safe) {
    persistReturnUrl(safe);
    params.set("returnUrl", safe);
  }
  const q = params.toString();
  return q ? `/login?${q}` : "/login";
}

export function captureCurrentReturnUrl(): string {
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (path.startsWith("/login")) {
    const existing = getSafeReturnUrl(
      new URLSearchParams(window.location.search).get("returnUrl")
    );
    return existing ?? "/dashboard";
  }
  return path;
}
